/**
 * Check a password against the HaveIBeenPwned API using k-anonymity.
 * Only the first 5 characters of the SHA-1 hash are sent — the full password
 * never leaves the browser.
 *
 * Returns the number of times this password has appeared in known data breaches.
 * Returns 0 if the password is not found or if the HIBP API is unavailable.
 */
export async function checkBreachedPassword(password: string): Promise<number> {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest("SHA-1", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, "0")).join("").toUpperCase();

    const prefix = hashHex.slice(0, 5);
    const suffix = hashHex.slice(5);

    const response = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`, {
      headers: { "Add-Padding": "true" },
    });

    if (!response.ok) return 0; // HIBP unavailable — don't block

    const text = await response.text();

    for (const line of text.split("\n")) {
      const [hash, countStr] = line.split(":");
      if (hash && hash.trim() === suffix) {
        return parseInt(countStr.trim(), 10);
      }
    }

    return 0;
  } catch {
    return 0; // Network error or unsupported env — don't block
  }
}
